/* eslint-disable no-empty */
// utils
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { FaTh, FaThList } from 'react-icons/fa';
import { IoCloseSharp, IoFilterOutline } from 'react-icons/io5';
import { useAuth } from '../../components/context/Info';
import WhatsAppComponent from '../../components/fixed/whatsapp/WhatsappComponent';
import PopUpComponent from '../../components/popUp/PopUpComponent';
import { getDefaultServerSideProps } from '../../lib/server/site-props';
import { getCompany, getCompanySearch } from '../../services/selectors';
import { formatSelects, isColorLight, useWindowSize } from '../../utils/FunctionsGlobals';
import { appendCodsiteQuery, buildCanonical } from '../../utils/url';
import { juntarBuscasDeEmpreendimentos, tipoPorIdHomonimo } from '../../utils/tiposHomonimos';
// Filtros, selos, botões e lista de /condominios usam as MESMAS classes do /imoveis.
import listStyles from '../imoveis/components/pieces/list/ListPiecesProperties.module.css';
import WelcomePiecesProperties2 from '../imoveis/components/pieces/properties/welcome2/WelcomePiecesProperties';
import styles from './Company.module.css';
import { ehModeloHorizonte, varsHorizonteInterna } from '../../components/pieces/horizonte/horizonte';
import { ehModeloAurora, varsAurora } from '../../components/pieces/aurora/aurora';
import { ehModeloPanorama, varsPanorama } from '../../components/pieces/panorama/panorama';
import { ehModeloLarissa, varsLarissaInterna } from '../../components/pieces/larissa/larissa';
import { ehModeloMetropole, varsMetropoleInterna } from '../../components/pieces/metropole/metropole';
import { ehModeloInternacional, varsInternacionalPagina } from '../../components/pieces/internacional/internacionalInternas';
import inInterna from '../../components/pieces/internacional/InternacionalInterna.module.css';
import { ehModeloNorte, varsNortePagina } from '../../components/pieces/norte/norteInternas';
import ntInterna from '../../components/pieces/norte/NorteInterna.module.css';
import '../../components/pieces/norte/NorteFonte.module.css';
import { paginaInternaAtelier, siteComPaletaAtelier, varsAtelier } from '../../components/pieces/atelier/atelier';
import atInterna from '../../components/pieces/atelier/AtelierInterna.module.css';
import type { CondominioFilters } from './components/pieces/filters/FiltrosCondominios';
import FiltrosCondominios from './components/pieces/filters/FiltrosCondominios';
import ListPiecesCondominio from './components/pieces/list/ListPiecesCondominio';
import { cardCondominioAP, coresCondominioAP, varsCondominioAP } from './components/pieces/list/ListPiecesCondominio';
import { cardCondominioHA } from './components/pieces/list/ListPiecesCondominio';
import { cardCondominioLr, coresCondominioLr } from './components/pieces/list/ListPiecesCondominio';
import { cardCondominioMt, coresCondominioMt } from './components/pieces/list/ListPiecesCondominio';
import { cardCondominioIn, coresCondominioIn } from './components/pieces/list/ListPiecesCondominio';
import { cardCondominioNt, coresCondominioNt } from './components/pieces/list/ListPiecesCondominio';
import { ehModeloCampo, varsCampoPagina } from '../../components/pieces/campo/campoInternas';
import cpInterna from '../../components/pieces/campo/CampoInterna.module.css';
import { cardCondominioCp, coresCondominioCp } from './components/pieces/list/ListPiecesCondominio';

const AboutPieces = dynamic(() => import('../../components/pieces/about/AboutPieces'), { ssr: false });
const AboutPieces1 = dynamic(() => import('../../components/pieces/about1/AboutPieces1'), { ssr: false });
import { NEW_RODAPE } from '../../components/registry/home';
const AboutPieces2 = dynamic(() => import('../../components/pieces/about1/AboutPieces2'), { ssr: false });
const MegaFooter = dynamic(() => import('../../components/pieces/mega-footer/MegaFooter'), { ssr: false });
const EditorialFooter = dynamic(() => import('../../components/pieces/editorial-footer/EditorialFooter'), { ssr: false });

const QTD_PAGINA = 12;

export const getServerSideProps = getDefaultServerSideProps;

const EMPTY_FILTERS: CondominioFilters = {
    nome: '',
    cidade: '',
    bairro: '',
    tipo: '',
    preco_min: '',
    preco_max: ''
};

const getQueryStringValue = (value: any) => {
    if (Array.isArray(value)) {
        return String(value[0] || '');
    }
    return String(value || '');
};

const normalizeFiltersFromQuery = (query: any): CondominioFilters => ({
    nome: getQueryStringValue(query?.nome),
    cidade: getQueryStringValue(query?.cidade),
    bairro: getQueryStringValue(query?.bairro),
    tipo: getQueryStringValue(query?.tipo),
    preco_min: getQueryStringValue(query?.preco_min).replace(/[^\d]/g, ''),
    preco_max: getQueryStringValue(query?.preco_max).replace(/[^\d]/g, '')
});

const hasAnyFilter = (filters: CondominioFilters) => {
    return Object.values(filters).some((value) => String(value || '').trim().length > 0);
};

// `?ids=1,2,3` — recorte FIXO da página, definido no destino do menu do tenant (ex.: um menu
// "Lançamentos" que só pode mostrar os empreendimentos escolhidos pela imobiliária). Não é
// filtro de usuário: não aparece nos chips, não é limpável e sobrevive à busca e ao "ver mais".
const normalizeIdsFromQuery = (query: any): number[] => {
    const raw = Array.isArray(query?.ids) ? query.ids.join(',') : String(query?.ids || '');

    return Array.from(
        new Set(
            raw
                .split(',')
                .map((token: string) => Number(String(token).trim()))
                .filter((value: number) => Number.isInteger(value) && value > 0)
        )
    );
};

const filtersToQuery = (filters: CondominioFilters) => {
    const query: Record<string, string> = {};

    if (filters.nome.trim()) query.nome = filters.nome.trim();
    if (filters.cidade.trim()) query.cidade = filters.cidade.trim();
    if (filters.bairro.trim()) query.bairro = filters.bairro.trim();
    if (filters.tipo.trim()) query.tipo = filters.tipo.trim();
    if (filters.preco_min.trim()) query.preco_min = filters.preco_min.trim();
    if (filters.preco_max.trim()) query.preco_max = filters.preco_max.trim();

    return query;
};

const toOptionalNumber = (value: string) => {
    const parsed = Number(value);
    if (!value || Number.isNaN(parsed) || parsed <= 0) {
        return undefined;
    }
    return parsed;
};

const formatFilterCurrency = (value: string) => {
    const amount = Number(value);
    if (!amount || Number.isNaN(amount)) {
        return value;
    }
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0
    }).format(amount);
};

const Company = () => {
    const router = useRouter();
    const size = useWindowSize();
    const isMobileFilter = size?.width ? size.width <= 900 : false;

    const {
        allDataHome,
        isMobile1,
        favorito,
        init_props
    } = useAuth();

    const allCity = allDataHome?.cidades;
    const allCities = allCity ? formatSelects(allCity) : [];
    // Atelier (topo 12): paleta do modelo (papel/tinta/acento); fora do gate devolve o MESMO objeto.
    const site = siteComPaletaAtelier(allDataHome?.site);
    // Modelos prontos #7 Aurora (topo 10) · #8 Horizonte (11) · #9 Atelier (12) · #10 Panorama (13): a página no visual
    // da home do modelo, como /imoveis e a ficha. Os 4: atributo data-modelo + CSS vars na raiz e regras
    // `[data-modelo='x'] .classe` nos módulos CSS da página; o Atelier soma a paleta (siteComPaletaAtelier) e a classe .interna do
    // AtelierInterna.module.css. Fora dos topos 10–13: modeloInterna/varsModelo vazios ⇒ DOM e estilo de sempre (zero diff).
    const modeloInterna = ehModeloHorizonte(site) ? 'horizonte' : ehModeloAurora(site) ? 'aurora' : ehModeloPanorama(site) ? 'panorama' : ehModeloLarissa(site) ? 'larissa' : ehModeloMetropole(site) ? 'metropole' : ehModeloInternacional(site) ? 'internacional' : ehModeloNorte(site) ? 'norte' : ehModeloCampo(site) ? 'campo' : paginaInternaAtelier(site) ? 'atelier' : undefined;
    const atelierInterna = paginaInternaAtelier(site);
    const varsModelo = (modeloInterna === 'horizonte' ? varsHorizonteInterna(site) : modeloInterna === 'aurora' ? varsAurora(site) : modeloInterna === 'panorama' ? varsPanorama(site) : modeloInterna === 'larissa' ? varsLarissaInterna(site) : modeloInterna === 'metropole' ? varsMetropoleInterna(site) : modeloInterna === 'internacional' ? varsInternacionalPagina(site) : modeloInterna === 'norte' ? varsNortePagina(site) : modeloInterna === 'campo' ? varsCampoPagina(site) : atelierInterna ? varsAtelier(site) : null) as CSSProperties | null;
    const [activeLargeFilter, setActiveLargeFilter] = useState(false);
    // Aurora (topo 10) / Panorama (topo 13): cores --lst-* do /imoveis do modelo, sem os inline legados
    // (chips, alternar grade/lista) e a GRADE com o card da home como padrão (card 7 / card 10).
    const coresAP = coresCondominioAP(site);
    const cardAP = cardCondominioAP(site, coresAP);
    // Horizonte (topo 11) / Atelier (topo 12): grade com o card do modelo como padrão (card 8 / card 9, como
    // no /imoveis); no Horizonte os chips e o alternar grade/lista perdem os inline legados (estilo no CSS).
    const cardHA = cardCondominioHA(site);
    const semInlineHz = modeloInterna === 'horizonte';
    // Larissa (topo 14): grade com o LarissaCard (card 11) como padrão; chips, "Filtrar" e alternar grade/lista
    // sem os inline legados (regras [data-modelo='larissa'] do Company.module.css); título Lora em dois tons.
    const cardLr = cardCondominioLr(site);
    const coresLr = coresCondominioLr(site);
    const semInlineLr = Boolean(coresLr);
    // Metrópole (topo 15 + card 12): grade com o MetropoleCard em modoEmpreendimento (card 12); mesmos inline legados fora (semInlineLrIn);
    // título em dois tons (fonte leve) e estilo nas regras [data-modelo='metropole'] do Company.module.css e do ListPiecesProperties.module.css.
    const cardMt = cardCondominioMt(site);
    const coresMt = coresCondominioMt(site);
    const semInlineMt = Boolean(coresMt);
    // Internacional (topo 16 + card 13): grade com o InternacionalCard em modoEmpreendimento; mesmos inline legados fora (semInlineLrIn).
    const cardIn = cardCondominioIn(site);
    const coresIn = coresCondominioIn(site);
    const semInlineIn = Boolean(coresIn);
    // Norte (topo 17 + card 14): idem, grade com o NorteCard em modoEmpreendimento; os mesmos inline legados fora.
    const cardNt = cardCondominioNt(site);
    const coresNt = coresCondominioNt(site);
    // Campo (topo 18 + card 15): idem, grade 3/2/1 com o CampoCard em modoEmpreendimento (o card da home), página de
    // PAPEL, filtros/chips/botões em PÍLULA (regras [data-modelo='campo'] do Company.module.css); inline legados fora.
    const cardCp = cardCondominioCp(site);
    const coresCp = coresCondominioCp(site);
    const semInlineLrIn = semInlineLr || semInlineMt || semInlineIn || Boolean(coresNt) || Boolean(coresCp);
    // ESPELHO de ListPiecesProperties.tsx (positionCard: lista horizontal por padrão nos
    // modelos 2+ no desktop, exceto cs2474) — manter em sincronia
    const initializeSquareCardsLayout = String(site?.id) === '2474';
    const [positionCard, setPositionCard] = useState<any>(
        Number(site?.modelo_card) > 1 && !isMobile && !initializeSquareCardsLayout && !cardAP && !cardHA && !cardLr && !cardMt && !cardIn && !cardNt && !cardCp ? 1 : 0
    );
    const isHorizontalCardLayout = Boolean(positionCard);
    // ESPELHO de ListPiecesProperties.tsx (corLetra2 do cabeçalho, selos e botões) — manter em sincronia
    const corLetra2 = coresAP ? coresAP.texto : coresLr ? coresLr.texto : coresMt ? coresMt.texto : coresIn ? coresIn.texto : coresNt ? coresNt.texto : coresCp ? coresCp.tinta : isColorLight(site?.cor5) ? (site?.cor11 || '#111827') : (site?.cor13 || '#ffffff');
    const stylesTags = coresAP || semInlineHz || semInlineLrIn ? undefined : {
        color: corLetra2,
        border: `2px solid ${corLetra2}`,
    };
    const marginIconTag = coresAP || semInlineHz || semInlineLrIn ? undefined : { marginTop: 3 };
    const [codsite] = useState<number>(init_props?.codsite);
    const [origin] = useState<any>(init_props?.prodOrigin);
    const codsite2 = init_props?.codsite;

    const [allCondominios, setAllCondominios] = useState<any[]>([]);
    const [paginaAtual, setPaginaAtual] = useState<number>(0);
    const [totalCondominios, setTotalCondominios] = useState<number>(0);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [loadingInitial, setLoadingInitial] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [draftFilters, setDraftFilters] = useState<CondominioFilters>(EMPTY_FILTERS);
    const [activeFilters, setActiveFilters] = useState<CondominioFilters>(EMPTY_FILTERS);
    // String (e não array) porque é dependência de hook: `[1,2]` novo a cada render reexecutaria
    // a busca em loop.
    const idsKey = normalizeIdsFromQuery(router.query).join(',');
    const urlSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const allCondominiosRef = useRef<any[]>([]);

    useEffect(() => {
        allCondominiosRef.current = allCondominios;
    }, [allCondominios]);

    const temFiltroAtivo = hasAnyFilter(activeFilters);
    const totalResultados = totalCondominios > 0 ? totalCondominios : allCondominios.length;
    const canonicalUrl = buildCanonical(site?.site || init_props?.prodOrigin, router.asPath || '/condominios');

    const allTypesMap = useMemo(() => {
        const types: Record<string, string> = {};
        allDataHome?.tipos?.forEach((tipo: any) => {
            if (tipo?.id != null && tipo?.nome) {
                types[String(tipo.id)] = tipo.nome;
                // tipo unificado (cs2644): link antigo com o id do xará continua mostrando o nome
                tipo?.ids_homonimos?.forEach((id: any) => { types[String(id)] = tipo.nome; });
            }
        });
        return types;
    }, [allDataHome?.tipos]);

    const updateFilters = useCallback((patch: Partial<CondominioFilters>) => {
        setDraftFilters((prev) => ({
            ...prev,
            ...patch
        }));
    }, []);

    const removeFilter = useCallback((field: keyof CondominioFilters) => {
        setDraftFilters((prev) => ({
            ...prev,
            [field]: '',
            // bairro depende da cidade: tirar a cidade tira o bairro junto (como no filtro lateral)
            ...(field === 'cidade' ? { bairro: '' } : {})
        }));
    }, []);

    const clearFilters = useCallback(() => {
        setDraftFilters(EMPTY_FILTERS);
    }, []);

    // lido por ref: não entra nas dependências do loadEmpreendimentos (não refaz a busca dos outros sites)
    const tiposRef = useRef<any>(allDataHome?.tipos);
    tiposRef.current = allDataHome?.tipos;

    const mergeUniqueEmpreendimentos = useCallback((items: any[]) => {
        const map = new Map<number, any>();
        items.forEach((item: any) => {
            if (item?.id != null && !map.has(item.id)) {
                map.set(item.id, item);
            }
        });
        return Array.from(map.values());
    }, []);

    const loadEmpreendimentos = useCallback(async (page: number, append: boolean, filters: CondominioFilters) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoadingInitial(true);
        }

        const idsSelecionados = idsKey ? idsKey.split(',').map(Number) : [];

        try {
            const buscar = (tipo: number | undefined) => getCompanySearch(
                page,
                QTD_PAGINA,
                filters.nome || undefined,
                filters.cidade || undefined,
                filters.bairro || undefined,
                toOptionalNumber(filters.preco_min),
                toOptionalNumber(filters.preco_max),
                tipo,
                origin,
                codsite,
                undefined,
                idsSelecionados
            );
            // Tipo unificado (cs2644, utils/tiposHomonimos.ts): a consulta aceita um tipo por vez,
            // então busca cada xará e junta. Fora dele é a mesma chamada única de sempre.
            const tipoFiltro = toOptionalNumber(filters.tipo);
            const tiposXaras: any[] = tipoPorIdHomonimo(tiposRef.current, tipoFiltro)?.ids_homonimos || [];
            const data: any = tiposXaras.length > 1
                ? juntarBuscasDeEmpreendimentos(
                    await Promise.all(tiposXaras.map((tipo) => buscar(Number(tipo)))),
                    page,
                    QTD_PAGINA
                )
                : await buscar(tipoFiltro);

            const response = data?.empreendimentos_busca;
            const empreendimentos = response?.empreendimentos || [];
            const count = Number(response?.count || 0);
            const hasMoreByCount = count > (page * QTD_PAGINA);
            const hasMoreByPageSize = Array.isArray(empreendimentos) && empreendimentos.length >= QTD_PAGINA;

            if (response && Array.isArray(empreendimentos)) {
                const previousList = append ? allCondominiosRef.current : [];
                const nextList = append
                    ? mergeUniqueEmpreendimentos([...previousList, ...empreendimentos])
                    : empreendimentos;
                const hasMoreFromServer = typeof response?.temMais === 'boolean'
                    ? response.temMais
                    : hasMoreByCount || hasMoreByPageSize;
                const shouldKeepServerCount = nextList.length > 0 || hasMoreFromServer;

                setAllCondominios(nextList);
                setTotalCondominios(
                    shouldKeepServerCount && count > 0
                            ? count
                            : nextList.length
                );
                setHasMore(hasMoreFromServer);
                setPaginaAtual(page);
                return;
            }

            if (!append && !hasAnyFilter(filters)) {
                const fallback = await getCompany('', origin, codsite, true);
                const legacyEmpreendimentos = (fallback?.empreendimentos || []).filter(
                    (item: any) => !idsSelecionados.length || idsSelecionados.includes(Number(item?.id))
                );
                setAllCondominios(legacyEmpreendimentos);
                setTotalCondominios(legacyEmpreendimentos.length);
                setHasMore(false);
                setPaginaAtual(1);
                return;
            }

            if (!append) {
                setAllCondominios([]);
                setTotalCondominios(0);
                setHasMore(false);
                setPaginaAtual(1);
            }
        } catch (error) {
            if (!append) {
                setAllCondominios([]);
                setTotalCondominios(0);
            }
            setHasMore(false);
        } finally {
            if (append) {
                setLoadingMore(false);
            } else {
                setLoadingInitial(false);
            }
        }
    }, [codsite, idsKey, mergeUniqueEmpreendimentos, origin]);

    const handleLoadMore = useCallback(() => {
        if (loadingInitial || loadingMore || !hasMore) {
            return;
        }
        loadEmpreendimentos(paginaAtual + 1, true, activeFilters);
    }, [activeFilters, hasMore, loadEmpreendimentos, loadingInitial, loadingMore, paginaAtual]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        const filtersFromUrl = normalizeFiltersFromQuery(router.query);
        const serialized = JSON.stringify(filtersFromUrl);

        setDraftFilters((prev) => JSON.stringify(prev) === serialized ? prev : filtersFromUrl);
        setActiveFilters((prev) => JSON.stringify(prev) === serialized ? prev : filtersFromUrl);
    }, [router.isReady, router.asPath, router.query]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        if (urlSyncTimeoutRef.current) {
            clearTimeout(urlSyncTimeoutRef.current);
        }

        urlSyncTimeoutRef.current = setTimeout(() => {
            // `ids` entra nos DOIS lados: é recorte do menu, não filtro do usuário — sai da
            // comparação empatado e sobrevive à reescrita da URL (sem ele nos dois, a página
            // se reescreveria em loop OU perderia o recorte na primeira busca).
            const nextQuery = { ...filtersToQuery(draftFilters), ...(idsKey ? { ids: idsKey } : {}) };
            const currentQuery = {
                ...filtersToQuery(normalizeFiltersFromQuery(router.query)),
                ...(idsKey ? { ids: idsKey } : {})
            };

            if (JSON.stringify(nextQuery) !== JSON.stringify(currentQuery)) {
                const nextSearch = new URLSearchParams(nextQuery).toString();
                const nextHref = appendCodsiteQuery(
                    nextSearch ? `/condominios?${nextSearch}` : '/condominios',
                    codsite,
                    site?.site || init_props?.prodOrigin
                );
                router.replace(
                    nextHref,
                    undefined,
                    { shallow: true, scroll: false }
                );
            }
        }, 420);

        return () => {
            if (urlSyncTimeoutRef.current) {
                clearTimeout(urlSyncTimeoutRef.current);
            }
        };
    }, [codsite, draftFilters, idsKey, init_props?.prodOrigin, router, router.isReady, router.asPath, router.query, site?.site]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }
        setAllCondominios([]);
        setPaginaAtual(0);
        setTotalCondominios(0);
        setHasMore(false);
        loadEmpreendimentos(1, false, activeFilters);
    }, [activeFilters, loadEmpreendimentos, router.isReady]);

    useEffect(() => {
        if (!isMobileFilter && activeLargeFilter) {
            setActiveLargeFilter(false);
        }
    }, [activeLargeFilter, isMobileFilter]);

    const activeFilterChips = useMemo(() => {
        const chips: Array<{ key: keyof CondominioFilters; label: string }> = [];

        // Selos como no /imoveis: só o valor (cidade, bairro, tipo); os demais com rótulo curto.
        if (activeFilters.nome) chips.push({ key: 'nome', label: activeFilters.nome });
        if (activeFilters.cidade) chips.push({ key: 'cidade', label: activeFilters.cidade });
        if (activeFilters.bairro) chips.push({ key: 'bairro', label: activeFilters.bairro });
        if (activeFilters.tipo) {
            chips.push({
                key: 'tipo',
                label: allTypesMap[activeFilters.tipo] || activeFilters.tipo
            });
        }
        if (activeFilters.preco_min) chips.push({ key: 'preco_min', label: `Mín: ${formatFilterCurrency(activeFilters.preco_min)}` });
        if (activeFilters.preco_max) chips.push({ key: 'preco_max', label: `Máx: ${formatFilterCurrency(activeFilters.preco_max)}` });

        return chips;
    }, [activeFilters, allTypesMap]);

    const renderRodape = () => {
        // REGISTRY: variante nova de rodapé ANTES do switch legado — o lookup devolve
        // undefined para os modelos 1-5, então o diff nos ~300 sites é nulo.
        const RodapeNovo = NEW_RODAPE[Number(site?.rodape)];
        if (RodapeNovo) {
            return (
                <RodapeNovo
                    site={site}
                    allCities={allCities}
                    allCitiesTipes={allDataHome?.tipos}
                    codsite={codsite2}
                    origin={origin}
                    isMobile={isMobile1}
                    allRodape={allDataHome?.rodape}
                    favorito={favorito}
                />
            );
        }

        switch (site?.rodape) {
            case 1:
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        viewport={{ once: true, amount: 0.22 }}
                    >
                        <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito}/>
                    </motion.div>
                );
            case 2:
                return (
                    <AboutPieces allRodape={allDataHome?.rodape} site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1}/>
                );
            case 3:
                return (
                    <AboutPieces2 site={site} allRodape={allDataHome?.rodape}/>
                );
            case 4:
                return (
                    <MegaFooter
                        site={site}
                        allCities={allCities}
                        codsite={codsite2}
                        origin={origin}
                        isMobile={isMobile1}
                        favorito={favorito}
                        allRodape={allDataHome?.rodape}
                        fallback={<AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito}/>}
                    />
                );
            case 5:
                return (
                    <EditorialFooter
                        site={site}
                        allCities={allCities}
                        codsite={codsite2}
                        origin={origin}
                        isMobile={isMobile1}
                        favorito={favorito}
                        allRodape={allDataHome?.rodape}
                        fallback={<AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito}/>}
                    />
                );
            default:
            return (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, amount: 0.22 }}
                >
                    <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito}/>
                </motion.div>
            );
        }
    };

    return (
        <div className={`${styles.container}${atelierInterna ? ` ${atInterna.interna}` : ''}${modeloInterna === 'internacional' ? ` ${inInterna.interna}` : ''}${modeloInterna === 'norte' ? ` ${ntInterna.interna}` : ''}${modeloInterna === 'campo' ? ` ${cpInterna.interna}` : ''}`} style={{background: site?.cor5, ...varsModelo, ...varsCondominioAP(coresAP)}} data-modelo={modeloInterna} data-fundo={semInlineLr ? (isColorLight(site?.cor5 || '#ffffff') ? 'claro' : 'escuro') : semInlineIn ? 'claro' : undefined}>
            <Head>
                <title>{`Empreendimentos | ${site?.titulo || site?.Dados?.empresa || 'Imobiliaria'}`}</title>
                <meta name="robots" content="index, follow" />
                <meta property="og:url" content={canonicalUrl} />
                <link rel="canonical" href={canonicalUrl} key="canonical" />
            </Head>
            <PopUpComponent />
            <WhatsAppComponent site={site} />
            <WelcomePiecesProperties2
                site={site}
                favorito={favorito}
                props={allDataHome}
            />

            {/* ESPELHO de ListPiecesProperties.tsx (overlay do filtro no mobile, cabeçalho com
                skeleton e selos, botão Filtrar e alternância grade/lista) — manter em sincronia */}
            {isMobileFilter && activeLargeFilter ? (
                <div className={listStyles.container_large_filter} data-nosnippet>
                    <FiltrosCondominios
                        site={site}
                        allDataHome={allDataHome}
                        codsite={codsite}
                        origin={origin}
                        filtros={draftFilters}
                        onChange={updateFilters}
                        activeLargeFilter
                        onFechar={() => setActiveLargeFilter(false)}
                    />
                </div>
            ) : null}
            <div className={listStyles.container_header}>
                {loadingInitial ?
                    <Box sx={{ height: 87, width: '100%' }}>
                        <Skeleton variant="rectangular" width="40%" height="47px" style={{borderRadius: "10px", margin: 5}}/>
                        <Skeleton variant="rectangular" width="20%" height="30px" style={{borderRadius: "10px", margin: 5}}/>
                    </Box>
                :
                    <>
                        <h1 className={styles.qtd} style={{color: corLetra2}}>
                            Empreendimentos
                            {/* Larissa: 2º tom (cinza, serifa) com a cidade filtrada — o título do modelo é em duas tonalidades */}
                            {semInlineLrIn && activeFilters?.cidade ? <span className={styles.qtdSuave}> em {activeFilters.cidade}</span> : null}
                        </h1>
                        <h2 className={styles.qtd1} style={{color: corLetra2}}>
                            <span data-nosnippet>{`${totalResultados} ${totalResultados > 1 ? 'Encontrados' : 'Encontrado'}`}</span>
                        </h2>
                    </>
                }
                {activeFilterChips.length > 0 ? (
                    <div className={listStyles.adjust_tags} data-nosnippet>
                        {activeFilterChips.map((chip, index) => (
                            <motion.div
                                key={chip.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 * index }}
                            >
                                <button
                                    aria-label={`Botão para remover o filtro ${chip.label}`}
                                    title={`Clique para remover o filtro ${chip.label}`}
                                    type="button"
                                    className={listStyles.tags}
                                    style={stylesTags}
                                    onClick={() => removeFilter(chip.key)}
                                >
                                    {chip.label}
                                    <IoCloseSharp
                                        size={16}
                                        color={coresAP || semInlineLrIn ? 'currentColor' : corLetra2}
                                        style={marginIconTag}
                                    />
                                </button>
                            </motion.div>
                        ))}
                        <motion.div
                            key="limpar_filtros"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 * activeFilterChips.length }}
                        >
                            <button
                                aria-label="Botão para limpar todos os filtros"
                                title="Clique para limpar todos os filtros"
                                type="button"
                                className={listStyles.tags1}
                                style={stylesTags}
                                onClick={clearFilters}
                            >
                                Limpar filtros
                            </button>
                        </motion.div>
                    </div>
                ) : null}
            </div>
            <div className={listStyles.container_select} data-nosnippet>
                {isMobileFilter ? (
                    <button
                        aria-label={`Botão para abrir filtros personalizados`}
                        title={`Clique para abrir filtros personalizados`}
                        type="button"
                        className={listStyles.mapa_button}
                        style={semInlineLrIn ? undefined : {borderColor: corLetra2,color: corLetra2}}
                        onClick={() => setActiveLargeFilter(true)}
                    >
                        <IoFilterOutline
                            size={semInlineLrIn ? 20 : 24}
                            color={semInlineLrIn ? 'currentColor' : corLetra2}
                            style={{ marginRight: 5 }}
                        />
                        Filtrar
                    </button>
                ) : null}
                { !isMobile && size?.width > 1150 ?
                    <button
                        aria-label={`Botão para alterar apresentação dos cards de empreendimentos`}
                        title={`Clique para alterar apresentação dos cards de empreendimentos`}
                        type="button" onClick={() => positionCard ? setPositionCard(0) : setPositionCard(1)} className={coresAP || semInlineHz || semInlineLrIn ? listStyles.alternar_layout : undefined} style={coresAP || semInlineHz || semInlineLrIn ? undefined : {background: "none",margin: "0px 13px 6px 5px",border: "none",padding: "0px"}}>
                        { isHorizontalCardLayout ?
                            <FaTh
                                size={25}
                                color={corLetra2}
                                style={{margin: '0px', cursor: 'pointer'}}
                            />
                        :
                            <FaThList
                                size={25}
                                color={corLetra2}
                                style={{margin: '0px',  cursor: 'pointer'}}
                            />
                        }
                    </button>
                : null }
            </div>
            <div className={listStyles.container_all_list}>
                <div className={listStyles.geral}>
                    {size?.width > 900 ? (
                        <FiltrosCondominios
                            site={site}
                            allDataHome={allDataHome}
                            codsite={codsite}
                            origin={origin}
                            filtros={draftFilters}
                            onChange={updateFilters}
                        />
                    ) : null}
                </div>
                <ListPiecesCondominio
                    allCompany={allCondominios}
                    site={site}
                    allCities={allCity}
                    origin={origin}
                    codsite={codsite}
                    isHorizontal={isHorizontalCardLayout}
                    onLoadMore={handleLoadMore}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    loadingInitial={loadingInitial}
                    temFiltro={temFiltroAtivo}
                    onLimparFiltros={clearFilters}
                />
            </div>
            { renderRodape() }
        </div>
    );
};

export default Company;
